import { useNavigate } from "react-router-dom";
import { Controller, useForm } from "react-hook-form";
import { BaseLayout } from "../../shared/layouts/base-layout-page";
import {
  Box,
  FormControl,
  FormControlLabel,
  FormLabel,
  Radio,
  RadioGroup,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { ICnpjData } from "../../shared/components/buscarCNPJ/buscar-cnpj";
import { PessoaJuridicaFormPart } from "../../shared/components/formulario/parts/PessoaJuridicaFormPart/pessoa-juridica.part";
import { PessoaFisicaFormPart } from "../../shared/components/formulario/parts/PessoaFisicaFormPart/pessoa-fisica.part";
import { useEffect, useState } from "react";
import {
  limpaCamposPsFisica,
  limpaCamposPsJuridica,
} from "../../shared/utils/limpa-campo-form/limpa-campos.part";
import { EnderecoFormPart } from "../../shared/components/formulario/parts/EnderecoFormPart/enderecoform.part";
import { SelectObra } from "../../shared/components/formulario/input-select-obra/selectObra";
import { SelectCargo } from "../../shared/components/formulario/input-select-cargo/selectCargo";
import InputDescricao from "../../shared/components/formulario/input-descricao/inputDescricao";
import { CriarFuncionarioContract } from "./contracts/criar-funcionario.contract";
import {
  CriarFuncionarioFormData,
  criarFuncionarioSchema,
} from "./schema/criar-funcionario.schema";
import { yupResolver } from "@hookform/resolvers/yup";
import { useMutation } from "@tanstack/react-query";
import { funcionarioService } from "./service/funcionario.service";
import { toast } from "react-toastify";
import useLoadingStore from "../../shared/store/loading.store";
import { ContaBancariaFormPart } from "../../shared/components/formulario/parts/ContaBancariaFormPart/contaBancariaform.part";
import InputData from "../../shared/components/formulario/input-data/inputData";
import {
  SectorEnum,
  TypeBondEnum,
} from "../../shared/utils/enum/enum";
import InputMascarado from "../../shared/components/formulario/input-mascarado/InputMascarado";
import { mascaraTelefone } from "../../shared/utils/mascaras";
import { toastError } from "../../shared/services/errors/erros-handler";
import { FerramanetasDetalhe } from "../../shared/components/ferramentasDaListagem/ferramentas-detalhe";
import InputSetores, {
  SectorOption,
} from "../../shared/components/formulario/inputSetores/inputSetor";
import { baseApiService } from "../../shared/services/api/axios-config/axios-config";
import { useSectorById } from "../../shared/components/formulario/inputSetores/use-sector";
import { mascaraMoedaReal } from "../../shared/utils/mascaras/mascaraMoedaReal";
import { SelectUnitOfMeasure, UnitOfMeasureEnum } from "../../shared/components/formulario/select/SelectUnitOfMeasure";
import { SelectTypeBond } from "../../shared/components/formulario/select/SelectTypeBond";
import Input from "../../shared/components/formulario/input/input";
import { InputSwitch } from "../../shared/components/formulario/input-switch/switch";

export function CriarFuncionario() {
  const methods = useForm<CriarFuncionarioContract>({
    resolver: yupResolver(criarFuncionarioSchema),
    defaultValues: {
      active: true,
      cnpj: "",
      cpf: "",
      isResponsible: false,
      sector: SectorEnum.RH, // valor default
      typeBond: TypeBondEnum.CLT, // valor default
      modality: UnitOfMeasureEnum.SQUARE_METER,
      idWork: "", // importante para o select obra
      positionId: "",
      admissionDate: new Date(), // já com a data atual
    },
  });

  const {
    handleSubmit,
    setValue,
    control,
    watch,
  } = methods;

  const navigate = useNavigate();
  const [valueOpcao, setOpicao] = useState<"cpf" | "cnpj">("cpf");
  const { setLoading } = useLoadingStore();

  useEffect(() => {
    if (valueOpcao !== "cpf") {
      limpaCamposPsJuridica({ methods });
    } else {
      limpaCamposPsFisica({ methods });
    }
  }, [valueOpcao, methods]);

  const { mutate } = useMutation({
    mutationFn: async (data: CriarFuncionarioFormData) => {
      setLoading(true, "Estamos quase lá! Só mais um momento...");
      await funcionarioService.criar(data);
    },
    onSuccess: () => {
      toast.success("Colaborador adicionado com sucesso");
      navigate("/colaboradores", { state: { reload: true } });
    },
    onError: (err: any) => {
      toastError(err);
    },
  });

  const preencherCampos = (dados: ICnpjData) => {
    setValue("corporateName", dados.razaoSocial || "");
    setValue("fantasyName", dados.fantasia || dados.razaoSocial || "");
  };

  const sector = watch("sector");
  const idWork = watch("idWork");

  const theme = useTheme();
  const mdDown = useMediaQuery(theme.breakpoints.down("md"));

  const handleChangeOpcao = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOpicao(event.target.value as "cpf" | "cnpj");
  };

  const setorSelecionado = useSectorById(sector);

  const dataDoDia = new Date();

  setValue("admissionDate", dataDoDia);

  return (
    <BaseLayout title="Adicior Colaborador">
      <FerramanetasDetalhe
        mostraBotaoApagar={false}
        mostraBotaoNovo={false}
        mostraBotaoSalvarEVoltar={false}
        mostraBotaoVoltar
        aoClicarBotaoSalvar={handleSubmit((formData) => {
          const { contact1, contact2, ...dataLimpa } = formData as any;
          mutate(dataLimpa);
        })}
        aoClicarBotaoVoltar={() => navigate("/colaboradores")}
      />
      <Box
        component="form"
        onSubmit={handleSubmit((formData) => {
          const { contact1, contact2, ...dataLimpa } = formData as any;
          mutate(dataLimpa);
        })}
        padding="20px 0"
        display="flex"
        flexDirection="column"
        height="calc(100% - 100px)"
        gap="20px"
      >
        {/* Opção CPF ou CNPJ */}
        <Box display="flex" flexDirection="column" gap="20px" width="100%">
          <FormControl>
            <FormLabel>Selecione CPF ou CNPJ</FormLabel>
            <RadioGroup value={valueOpcao} onChange={handleChangeOpcao} row>
              <FormControlLabel value="cnpj" control={<Radio />} label="CNPJ" />
              <FormControlLabel value="cpf" control={<Radio />} label="CPF" />
            </RadioGroup>
          </FormControl>

          {valueOpcao === "cnpj" ? (
            <PessoaJuridicaFormPart
              methods={methods}
              mdDown={mdDown}
              preencherCampos={preencherCampos}
            />
          ) : (
            <PessoaFisicaFormPart methods={methods} mdDown={mdDown} />
          )}
        </Box>

        <Box
          display="grid"
          gridTemplateColumns={mdDown ? "1fr 1fr" : "1fr 1fr"}
          gap="20px"
          width="100%"
        >
          <SelectTypeBond name="typeBond" methods={methods} />

          <InputSetores
            name="sector"
            label="Selecione um setor de Trabalho"
            methods={methods}
          />
        </Box>

        {/* Datas e Modalidade */}
        <Box
          display="grid"
          gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
          gap="20px"
          width="100%"
        >
          <InputData
            name="dateOfBirth"
            label="Data de Nascimento"
            methods={methods}
          />

          <SelectUnitOfMeasure label="Modelo de Trabalho" name={`modality`} control={control} />
        </Box>

        {/* Se setor for 'work', mostra obra e cargo */}
        {setorSelecionado?.origin === SectorEnum.WORK && (
          <Box
            display="grid"
            gridTemplateColumns={mdDown ? "1fr 1fr" : "1fr 1fr"}
            gap="20px"
            width="100%"
            flexWrap="wrap"
          >
            <Controller
              name="idWork"
              control={control}
              render={({ field }) => (
                <SelectObra
                  value={field.value ?? ""}
                  onChange={field.onChange}
                />
              )}
            />

            <Controller
              name="positionId"
              control={control}
              render={({ field }) => (
                <SelectCargo
                  value={field.value ?? ""}
                  onChange={field.onChange}
                  idObra={idWork || ""}
                />
              )}
            />
          </Box>
        )}

        <ContaBancariaFormPart methods={methods} mdDown={mdDown} />

        {/* Contato */}
        <Box>
          <Typography variant="h5" padding="20px 0">
            Contato:
          </Typography>
          <Box
            display="grid"
            gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
            gap="20px"
            width="100%"
            flexWrap="wrap"
          >
            <Input name="email" label="E-mail" methods={methods} type="email" />

            <InputMascarado
              name="phone"
              methods={methods}
              label="Telefone"
              mask={mascaraTelefone}
            />
          </Box>
        </Box>

        <EnderecoFormPart methods={methods} mdDown={mdDown} />

        {/* Ativo e Responsável */}
        <Box
          display="grid"
          gridTemplateColumns={mdDown ? "1fr 1fr" : "1fr 1fr 1fr"}
          gap="20px"
          width="100%"
          flexWrap="wrap"
          paddingTop="20px"
        >
          <InputSwitch name="active" label="Funcionário ativo" methods={methods} />
          <InputData
            name="admissionDate"
            label="Data de Admissão"
            methods={methods}
          />
          {setorSelecionado?.origin === SectorEnum.WORK && (
            <InputSwitch
              name="isResponsible"
              label="Funcionário é Responsável pela Obra?"
              methods={methods}
            />
          )}
        </Box>

        <Typography variant="h5" padding="20px 0">
          Observações:
        </Typography>
        <Box
          display="grid"
          gridTemplateColumns={mdDown ? "1fr" : "1fr 1fr"}
          gap="20px"
        >
          <Box sx={{ gridColumn: mdDown ? "auto" : "1 / -1" }}>
            <InputDescricao
              name="description"
              methods={methods}
              label="Descrição"
            />
          </Box>
          {!idWork && (
            <InputMascarado
              name="salary"
              methods={methods}
              label="Salário"
              mask={mascaraMoedaReal}
            />
          )}
        </Box>
      </Box>
    </BaseLayout>
  );
}
